import * as React from "react";
import {
  List,
  Datagrid,
  TextField,
  Edit,
  SimpleForm,
  SimpleShowLayout,
  Show,
  ArrayField,
  FunctionField,
  SavedQueriesList,
  FilterLiveSearch,
  WithListContext,
  ArrayInput,
  SimpleFormIterator,
  FormDataConsumer,
  useRecordContext,
} from "react-admin";
import { Driver } from "../models/Driver";
import { getTruckPlateColorLabel } from "../models/Truck";
import { TruckLength, TruckType } from "../utils/truck-utils";
import { Button, CardContent, Chip, Stack } from "@mui/material";
import HistoryIcon from "@mui/icons-material/History";
import { useNavigate } from "react-router-dom";
import DivisionInput from "./components/division-input";
import { AsideStyledCard, fixedRightAsideListSx, fixedRightAsideSx } from "./components/common";
import { CustomField } from "./fleets/fleets/common";
import { formatDate } from "../utils/common";
import UserContext from "../contexts/UserContext";
import { buildAppTrackPathByUserId } from "./app-track/app_track_utils";

/**
 * 跳转到该司机的货源行为数据记录。
 */
const AppTrackJumpButton: React.FC<{ stopPropagation?: boolean }> = ({
  stopPropagation = false,
}) => {
  const record = useRecordContext<Driver>();
  const navigate = useNavigate();
  const userId = record?.id ? String(record.id) : "";

  if (!userId) {
    return null;
  }

  return (
    <Button
      size="small"
      variant="outlined"
      startIcon={<HistoryIcon />}
      onClick={(event) => {
        if (stopPropagation) {
          event.stopPropagation();
        }
        navigate(buildAppTrackPathByUserId(userId));
      }}
    >
      行为数据
    </Button>
  );
};

const Aside = () => (
  <AsideStyledCard sx={fixedRightAsideSx}>
    <CardContent>
      <FilterLiveSearch source="name" label="姓名" alwaysOn />
      <FilterLiveSearch source="phone" label="手机号" alwaysOn />
      <FilterLiveSearch source="trucks.0.plate" label="车牌" alwaysOn />
      <FilterLiveSearch source="trucks.0.type" label="车型" alwaysOn />
      <FilterLiveSearch source="lat" label="经度" alwaysOn />
      <FilterLiveSearch source="lon" label="纬度" alwaysOn />
      <FilterLiveSearch source="distance" label="距离" alwaysOn />
      <SavedQueriesList />
    </CardContent>
  </AsideStyledCard>
);

export const DriverList: React.FC = (props) => (
  <List
    {...props}
    aside={<Aside />}
    sx={fixedRightAsideListSx}
  >
    <Datagrid rowClick="show" bulkActionButtons={false}>
      <TextField source="name" label="姓名" />
      <TextField source="phone" label="手机号" />
      <TextField source="trucks.0.plate" label="车牌" />
      <TextField source="trucks.0.type" label="车型(旧)" />
      <ArrayField source="subsRoutes" label="订阅专线">
        <WithListContext
          render={({ data }) => (
            <Stack direction="row" spacing={1}>
              {data?.map((_) => (
                <Chip key={_._id} label={`${_.from.city} - ${_.to.city}`} />
              ))}
            </Stack>
          )}
        />
      </ArrayField>
      <FunctionField
        source="trucks.0.plate_color"
        label="车牌颜色"
        render={(record: Driver) =>
          getTruckPlateColorLabel(record?.trucks?.[0]?.plate_color)
        }
      />
      <FunctionField
        source="trucks.0.truck_type"
        label="车型（新）"
        render={(record: Driver) => TruckType(record?.trucks?.[0]?.truck_type)}
      />
      <FunctionField
        source="trucks.0.truck_length"
        label="车长（新）"
        render={(record: Driver) =>
          TruckLength(record?.trucks?.[0]?.truck_length)
        }
      />
      <FunctionField
        label="操作"
        render={() => <AppTrackJumpButton stopPropagation />}
      />
    </Datagrid>
  </List>
);

export const DriverEdit: React.FC = (props) => {
  const { isScheduler } = React.useContext(UserContext);
  return isScheduler ? (
    <Edit
      {...props}
      transform={(_: { [x: string]: any; lastPosition: any }) => {
        const { id, subsRoutes } = _;
        return { id, subsRoutes };
      }}
    >
      <SimpleForm>
        <TextField source="name" label="姓名" />
        <FormDataConsumer>
          {({ formData }) =>
            formData.subsRoutes ? (
              <ArrayInput source="subsRoutes" label="订阅专线">
                <SimpleFormIterator inline>
                  <DivisionInput source="from" label="起点" />
                  <DivisionInput source="to" label="终点" />
                </SimpleFormIterator>
              </ArrayInput>
            ) : (
              <span />
            )
          }
        </FormDataConsumer>
      </SimpleForm>
    </Edit>
  ) : null;
};

export const DriverShow: React.FC = (props) => {
  const navigate = useNavigate();
  
  const handleGoBack = () => {
    // 使用 navigate(-1) 返回上一页，React Router 会自动保留之前页面的 location state
    navigate(-1);
  };
  
  return (
  <Show {...props}>
    <SimpleShowLayout>
      <TextField source="name" label="姓名" />
      <TextField source="nickname" label="昵称" />
      <TextField source="phone" label="手机号" />
      <ArrayField source="trucks" label="货车">
        <Datagrid bulkActionButtons={false}>
          <TextField source="plate" label="车牌" />
          <TextField source="type" label="车型" />
          <CustomField source="truck_length" label="车长" />
          <CustomField source="plate_color" label="车牌颜色" />
        </Datagrid>
      </ArrayField>
      <FunctionField
        label="订阅专线"
        render={(record: any) =>
          record.subsRoutes && record.subsRoutes.length > 0
            ? record.subsRoutes
                .map((route: any) => `${route.from.city} - ${route.to.city}`)
                .join(", ")
            : "无"
        }
      />
      <FunctionField
        label="总运单数"
        render={(record: any) => record.waybills?.length || 0}
      />
      <FunctionField
        label="最新位置"
        render={(record: any) =>
          record.positions && record.positions.length > 0
            ? `${record.positions[record.positions.length - 1].province} ${
                record.positions[record.positions.length - 1].city
              } (更新时间: 
                
                ${formatDate(
                  record.positions[record.positions.length - 1].updatedAt
                )})`
            : "无"
        }
      />
      <Stack direction="row" spacing={1.5} sx={{ mt: 2 }}>
        <AppTrackJumpButton />
        <Button variant="contained" color="primary" onClick={handleGoBack}>
          返回
        </Button>
      </Stack>
    </SimpleShowLayout>
  </Show>
  );
};